import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useFeatureEnabled } from "@/hooks/useOrganizationFeatures";
import { useOrgBasePath } from "@/hooks/useOrgNavigate";

interface FeatureGateProps {
  featureKey: string;
  children: React.ReactNode;
}

/**
 * Wraps route content and redirects to the dashboard when the
 * given feature flag is not enabled for the current organization.
 *
 * Use this for route-level gating (entire pages).
 * For conditional UI within a page, use `useFeatureEnabled()` directly.
 */
export function FeatureGate({ featureKey, children }: FeatureGateProps) {
  const { enabled, isLoading } = useFeatureEnabled(featureKey);
  const orgBasePath = useOrgBasePath();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full" role="status" aria-label="Checking feature access">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!enabled) {
    return <Navigate to={orgBasePath ? `${orgBasePath}/dashboard` : '/org-select'} replace />;
  }

  return <>{children}</>;
}
